import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Input from "../components/form/Input";
import { signupFields } from "../constants/formFields";
import Cookies from "js-cookie";
import ButtonSecondary from "../components/buttons/ButtonSecondary";
import useAuthApi from "../hooks/api/useAuthApi";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { register } = useAuthApi();

  // Initialize form data from signupFields
  const [formData, setFormData] = useState(() => {
    const initialState = {};
    signupFields.forEach(field => {
      initialState[field.name] = "";
    });
    return initialState;
  });

  useEffect(() => {
    const accessToken = Cookies.get("AccessToken");
    if (accessToken) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const validateForm = () => {
    const requiredFields = signupFields.filter(field => field.isRequired);
    for (const field of requiredFields) {
      if (!formData[field.name] || formData[field.name].trim() === "") {
        toast.error(`${field.labelText} is required`);
        return false;
      }
    }

    const passwordField = signupFields.find(field =>
      field.type === "password" &&
      field.name === "password"
    );
    const confirmField = signupFields.find(field =>
      field.type === "password" &&
      (field.name.includes("confirm") || field.labelText.toLowerCase().includes("confirm"))
    );

    if (passwordField && confirmField) {
      const password = formData[passwordField.name];
      const confirmPassword = formData[confirmField.name];

      if (password !== confirmPassword) {
        toast.error("Passwords do not match");
        return false;
      }

      if (password && password.length < 6) {
        toast.error("Password must be at least 6 characters long");
        return false;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    if (!agreedToTerms) {
      toast.error("You must agree to the Terms & Conditions");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload = {};
      signupFields.forEach(field => {
        if (formData[field.name]) {
          payload[field.name] = formData[field.name];
        }
      });

      payload.terms_accepted = agreedToTerms;

      const response = await register(payload);

      if (response && response.status === 200) {
        toast.success("Account created successfully! Redirecting to login...");

        setTimeout(() => {
          navigate("/login");
        }, 1500);
      }

    } catch (error) {
      if (error.response && error.response.status === 422) {
        const errors = error.response.data.errors;
        if (errors && typeof errors === 'object') {
          Object.values(errors).forEach((errorArray) => {
            if (Array.isArray(errorArray)) {
              errorArray.forEach(msg => toast.error(msg));
            }
          });
        }
      } else if (error.response && error.response.data && error.response.data.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("An error occurred during registration. Please try again.");
        console.error("Registration error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card-login bg-white rounded-lg mobXS:mx-6 tabMD:mx-6 my-10 border desktopXl:w-1/4 desktop:w-1/2 desktop:mx-auto">
      <h6 className="text-h6 text-dark4 text-start py-12 px-8">
        Create your account
      </h6>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-px desktop:w-full flex justify-center">
          <div className="mobXS:w-full tabMD:w-1/2 desktop:w-full tabMD:items-center">
            {signupFields.map((field) => (
              <Input
                key={field.id}
                handleChange={handleChange}
                value={formData[field.name] || ""}
                labelText={field.labelText}
                labelFor={field.labelFor}
                id={field.id}
                name={field.name}
                type={field.type}
                isRequired={field.isRequired}
                placeholder={
                  typeof field.placeholder === "string"
                    ? field.placeholder
                    : undefined
                }
                customClass="rounded-lg"
                disabled={isLoading}
              />
            ))}

            <div className="flex items-center mx-6 mt-4">
              <input
                type="checkbox"
                className="rounded border-stroke text-primary cursor-pointer"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                disabled={isLoading}
                id="terms-checkbox"
              />
              <label
                htmlFor="terms-checkbox"
                className="text-dark1 text-bodyMM ml-3 cursor-pointer"
              >
                I agree with{" "}
                <a
                  href="/terms-conditions"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms & Conditions
                </a>
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-y-6 px-6 mobXS:items-start mobXS:justify-self-start tabMD:items-center tabMD:justify-self-center">
          <ButtonSecondary
            text={isLoading ? "Creating account..." : "Create account"}
            type="submit"
            bgColor="bg-secondary"
            color="text-white"
            customClass="rounded-[25px]"
            padding="mobXS:py-[10px] mobXS:px-[24px] tabMD:py-[10px] tabMD:px-[24px]"
            fontsize="text-bodyMR"
            width="mobXS:w-full tabMD:w-1/2 desktop:w-full"
            disabled={isLoading}
          />
        </div>

        <div className="flex justify-center pb-10">
          <a
            className="text-primary text-bodyMM hover:underline"
            href="/login"
          >
            Already have an account?
          </a>
        </div>
      </form>

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default RegisterPage;